import React from 'react'
import { useIntl } from 'react-intl'
import { Add } from '@mui/icons-material'
import {
    Box,
    Button,
    Divider,
    FormHelperText,
    MenuItem,
    Select,
    useTheme,
} from '@mui/material'
import ConfirmDelete from '../../../../../components/dialogs/ConfirmDelete'
import { CustomRichTextEditor } from '../../../../../components/customRichTextEditor'
import { ALPHABET } from '../../../../../utils/constants'
import { QuestionState } from '../../../../../features/types'

interface Props {
    formik: any
    questionIndex: number
    question: QuestionState
}

const ComplianceItems: React.FC<Props> = ({
    formik,
    questionIndex,
    question,
}) => {
    const theme = useTheme()
    const { formatMessage } = useIntl()

    const styles = {
        wrapper: {
            maxWidth: 600,
            pt: 2,
        },

        itemBox: {
            display: 'flex',
            alignItems: 'start',
            mt: 3,
        },

        textEditor: {
            width: '100%',
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: '8px',
            mr: 1,
        },

        contentBox: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            mr: 2,
        },

        select: {
            minWidth: '120px',
            mt: 1,
        },

        addItem: {
            display: 'flex',
            mt: 2,
        },
    }

    const complianceItems = question.complianceItems || []

    const answerLabel = (index: number) => {
        const content = question.answers[index]?.content || ''
        const text = content.replace(/<[^>]*>/g, '').trim()

        return `${ALPHABET[index].name}) ${text.substring(0, 30)}`
    }

    const handleAdd = () => {
        formik.setFieldValue(`questions[${questionIndex}].complianceItems`, [
            ...complianceItems,
            {
                complianceItemId: Date.now(),
                content: '',
                position: 0,
                correctAnswerIndexes: [],
            },
        ])
    }

    const handleDelete = (index: number) => {
        formik.setFieldValue(
            `questions[${questionIndex}].complianceItems`,
            complianceItems.filter((item, itemIndex) => itemIndex !== index),
        )
    }

    const handleSelectAnswer = (index: number, answerIndex: number) => {
        formik.setFieldValue(
            `questions[${questionIndex}].complianceItems[${index}].correctAnswerIndexes`,
            [answerIndex],
        )
    }

    return (
        <Box sx={styles.wrapper}>
            {complianceItems.map((item, index) => (
                <Box key={item.id || item.complianceItemId}>
                    <Box sx={styles.itemBox}>
                        <Box sx={styles.contentBox}>
                            <Box
                                className="CustomEditor"
                                sx={styles.textEditor}
                            >
                                <CustomRichTextEditor
                                    value={item.content}
                                    inline={true}
                                    editable={true}
                                    onEditorChange={(text) => {
                                        formik.handleChange({
                                            target: {
                                                name: `questions[${questionIndex}].complianceItems[${index}].content`,
                                                value: text,
                                            },
                                        })
                                    }}
                                />
                            </Box>

                            <Select
                                displayEmpty
                                size="small"
                                sx={styles.select}
                                value={
                                    item.correctAnswerIndexes?.length
                                        ? item.correctAnswerIndexes[0]
                                        : ''
                                }
                                onChange={(event) =>
                                    handleSelectAnswer(
                                        index,
                                        Number(event.target.value),
                                    )
                                }
                            >
                                <MenuItem
                                    value=""
                                    disabled
                                >
                                    {formatMessage({ id: 'chooseAnswerOption' })}
                                </MenuItem>

                                {question.answers.map(
                                    (answer, answerIndex) => (
                                        <MenuItem
                                            key={answer.id || answer.answerId}
                                            value={answerIndex}
                                        >
                                            {answerLabel(answerIndex)}
                                        </MenuItem>
                                    ),
                                )}
                            </Select>

                            <FormHelperText sx={{ color: 'red', ml: 1 }}>
                                {
                                    formik.errors?.questions?.[questionIndex]
                                        ?.complianceItems?.[index]?.content
                                }
                            </FormHelperText>
                        </Box>

                        <Box sx={{ paddingTop: '8px' }}>
                            <ConfirmDelete
                                confirmAction={() => handleDelete(index)}
                            />
                        </Box>
                    </Box>

                    {index !== complianceItems.length - 1 && <Divider />}
                </Box>
            ))}

            <Box sx={styles.addItem}>
                <Button
                    endIcon={<Add />}
                    sx={{ marginLeft: 'auto' }}
                    onClick={handleAdd}
                >
                    {formatMessage({ id: 'addMatchingItem' })}
                </Button>
            </Box>
        </Box>
    )
}

export default ComplianceItems
